import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useStore } from 'zustand';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useLeftButtonEvents } from './useLeftButtonEvents';
import { useRightButtonEvents } from './useRightButtonEvents';
import { useMouseMoveEvents } from './useMouseMoveEvents';
import { useCopyPasteEvents } from './useCopyPasteEvents';
import { SelectedPoint, CopiedObject } from '../types/eventTypes';
import { v4 as uuidv4 } from 'uuid';
import { Point } from '../../types/Point';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useAnnotationStore } from '../../components/annotations/annotationStore';
import { useAnnotationActions } from '../../components/annotations/useAnnotationActions';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';
import { markAssemblyUpdating, markAssembliesUpdated } from '../../utils/assemblyUpdateTracker';

export const useMouseEvents = (svgRef: React.RefObject<SVGSVGElement>) => {
  // Get cursor position from store
  const cursorPosition = useStore(useUnityBuildStore).cursorPosition;
  const setCursorPosition = useStore(useUnityBuildStore).setCursorPosition;

  // Get annotation state
  const { isAnnotating, setIsAnnotating } = useStore(useAnnotationStore);
  const { createAnnotation } = useAnnotationActions();

  // Create refs for cursor position tracking
  const cursorPositionRef = useRef({
    x: cursorPosition.x,
    y: cursorPosition.y,
  });
  const pastePositionRef = useRef<{ x: number; y: number } | null>(null);

  // Selection and drag state
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [selectedPoints, setSelectedPointsLocal] = useState<SelectedPoint[]>([]);

  // Copy and paste state
  const [copiedObjects, setCopiedObjects] = useState<CopiedObject[]>([]);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showPastePreview, setShowPastePreview] = useState(false);
  const [, setPastePreviewOffset] = useState({ x: 0, y: 0 });
  const pastePreviewPoints = useRef<Array<any>>([]);

  // Get assemblies list directly from store to ensure reactivity
  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  const { isLassoMode, isLassoDrawing, setIsLassoDrawing } = useStore(useUnityBuildStore);
  const assembliesList = useMemo(() => {
    if (!filteredCountedAssemblies?.filteredAssemblies) return [];

    // Extract all assemblies from all filtered assembly groups (filtered by current objectid/page)
    return filteredCountedAssemblies.filteredAssemblies.flatMap(
      (group: any) => group.assembliesList || []
    );
  }, [filteredCountedAssemblies]);

  // Import the required hooks properly at the component level
  const { submitAssembly } =
    require('../../hooks/useSubmitTakeoffAssembly').useSubmitTakeoffAssembly();

  // Initialize all specialized hooks
  const { handleTakeoffButton0MouseEvents } = useLeftButtonEvents({
    svgRef,
    cursorPositionRef,
    setCursorPosition,
    selectedPoints,
    setSelectedPointsLocal,
    setIsDragSelecting,
    setDragStartPosition,
    assembliesList,
    showPastePreview,
    pastePreviewPoints,
    pastePositionRef,
    isAnnotating,
    addNote: (x: number, y: number) => createAnnotation(x, y),
    setIsAnnotating,
  });

  const { handleTakeoffButton2MouseEvents } = useRightButtonEvents({
    svgRef,
    cursorPositionRef,
    pastePositionRef,
    selectedPoints,
    showPastePreview,
    pastePreviewPoints,
    setShowContextMenu,
    assembliesList,
    copiedObjects,
  });

  const { handleMouseMove } = useMouseMoveEvents({
    svgRef,
    cursorPositionRef,
    setCursorPosition,
    isDragSelecting,
    dragStartPosition,
    selectedPoints,
    setSelectedPointsLocal,
    showPastePreview,
    pastePreviewPoints,
    setPastePreviewOffset,
    pastePositionRef,
    assembliesList,
    copiedObjects,
  });

  const { handleCopy, handlePaste, activatePastePreview, renderPastePreview } = useCopyPasteEvents({
    cursorPositionRef,
    pastePositionRef,
    selectedPoints,
    setCopiedObjects,
    setShowPastePreview,
    setPastePreviewOffset,
    pastePreviewPoints,
    assembliesList,
    copiedObjects,
  });

  // Handle mouse up event (used for drag selection)
  const handleMouseUp = useCallback(() => {
    if (isDragSelecting) {
      setIsDragSelecting(false);
      try {
        document.dispatchEvent(new CustomEvent('annotations:drag-select-end'));
      } catch {}

      // Ensure the selected points are correctly set in the global store
      if (selectedPoints && selectedPoints.length > 0) {
        const { setSelectedPoints: setGlobalSelectedPoints } = useSelectionStore.getState();
        setGlobalSelectedPoints(selectedPoints);
      }

      // Clear current takeoff assembly after drag selection
      const { setCurrentTakeoffAssembly } = useAssemblyStore.getState();
      setCurrentTakeoffAssembly(null);
    }
    // End lasso drawing and finalize selection
    if (isLassoMode && isLassoDrawing) {
      setIsLassoDrawing(false);
    }
  }, [isDragSelecting, selectedPoints, isLassoDrawing, isLassoMode, setIsLassoDrawing]);

  // Handle keyboard events for copy/paste operations
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Handle copy (Ctrl+C)
      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      }

      // Handle paste (Ctrl+V)
      if (e.ctrlKey && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      }

      // Hide preview on Escape
      if (e.key === 'Escape' && showPastePreview) {
        setShowPastePreview(false);
      }
    },
    [handleCopy, handlePaste, showPastePreview]
  );

  // Function to clear paste preview
  const clearPastePreview = useCallback(() => {
    setShowPastePreview(false);
    pastePreviewPoints.current = [];
  }, []);

  // Add event listener for paste preview events from PointEditor
  useEffect(() => {
    const handlePastePreviewEvent = () => {
      // Always derive the freshest copied objects from the selection store
      const selectionStore = useSelectionStore.getState();
      const currentObject = useUnityBuildStore.getState().selectedObject;

      const latestCopied = selectionStore.copiedObjects || [];
      const hasLatest = selectionStore.hasCopiedItems && latestCopied.length > 0;

      if (!hasLatest) {
        return; // Nothing to preview
      }

      // In extend mode, ensure we're still on the same document
      if (selectionStore.copyMode === 'extend') {
        const originalObjectId = latestCopied[0]?.assemblyProperties?.objectid;
        if (originalObjectId && originalObjectId !== currentObject?.id) {
          console.log('[PASTE PREVIEW] Canceling paste preview in extend mode - document changed');
          selectionStore.clearCopiedItems();
          return;
        }
      }

      // Sync local state and activate preview with the freshest data
      setCopiedObjects(latestCopied);
      // Activate immediately with latest objects to avoid any stale closure
      activatePastePreview(latestCopied);
    };

    const handleEscapeEvent = () => {
      clearPastePreview();

      // CRITICAL: Always reset run flags when escape is pressed globally
      const unityStore = useUnityBuildStore.getState();
      unityStore.setHasActiveRun(false);
      unityStore.setHasActiveRunStarted(false);
      unityStore.setHasPointEditorInteraction(false);

      // Also clear currentTakeoffAssembly when escaping paste preview
      const assemblyStore = useAssemblyStore.getState();
      if (assemblyStore.currentTakeoffAssembly) {
        assemblyStore.setCurrentTakeoffAssembly(null);
      }

      // Ensure temporary points are cleared from Unity Build store
      unityStore.setTemporaryPoints([]);

      console.log('[ESCAPE] Reset all run flags and cleared assembly globally');
    };

    // Add listener for copy events to synchronize state
    const handleCopyEvent = (e: Event) => {
      // After a short delay to allow the copy operation to complete,
      // check if we have updated objects in the selection store
      setTimeout(() => {
        // Use handleCopy directly to ensure consistent behavior
        handleCopy();
      }, 50);
    };

    // Add handler for clear paste preview event
    const handleClearPastePreview = () => {
      clearPastePreview();
      pastePositionRef.current = null;
      (window as any).__pastePreviewActive = false;
      (window as any).__pastePositionFrozen = false;
      (window as any).__frozenPastePosition = null;
      (window as any).__currentPastePreviewOffset = null;
    };

    // Remove duplicate - using the existing handleEscapeEvent above

    // Add handler for paste event from PointEditor
    const handlePasteEvent = (e: Event) => {
      // Pass the event with its details to handlePaste
      handlePaste(e as CustomEvent);
    };

    // Add handler for delete operation
    const handleDeleteEvent = async () => {
      const { selectedPoints: pointsToDelete } = useSelectionStore.getState();
      if (!pointsToDelete || pointsToDelete.length === 0) {
        console.log('[DELETE DEBUG] No points selected for deletion');
        return;
      }

      console.log('[DELETE DEBUG] Deleting points:', pointsToDelete);
      console.log('[DELETE DEBUG] Available assemblies:', assembliesList);

      // Mark affected assemblies as updating BEFORE we start
      const affectedAssemblyIds = new Set<string>();
      pointsToDelete.forEach(point => {
        affectedAssemblyIds.add(point.assemblyId);
      });
      affectedAssemblyIds.forEach(id => markAssemblyUpdating(id));

      // Group selected points by assembly ID
      const pointsByAssembly = new Map();

      // Create a map of pointId to true for faster lookups
      const selectedPointIds = new Set(pointsToDelete.map(p => p.pointId));
      console.log('[DELETE DEBUG] Selected point IDs:', Array.from(selectedPointIds));

      // Track which assemblies need updating
      for (const assembly of assembliesList) {
        if (!assembly.pointsList) continue;

        // Check if this assembly has any selected points
        const hasSelectedPoints = assembly.pointsList.some((point: { pointid: string }) =>
          selectedPointIds.has(point.pointid)
        );

        console.log(
          `[DELETE DEBUG] Assembly ${assembly.assemblyid} has selected points:`,
          hasSelectedPoints
        );

        if (hasSelectedPoints) {
          // Get all non-selected points (points to keep)
          const remainingPoints = assembly.pointsList.filter(
            (point: { pointid: string }) => !selectedPointIds.has(point.pointid)
          );

          console.log(
            `[DELETE DEBUG] Assembly ${assembly.assemblyid} remaining points:`,
            remainingPoints.length
          );

          // Add to map of assemblies to update
          pointsByAssembly.set(assembly.assemblyid, {
            assembly,
            remainingPoints,
          });
        }
      }

      console.log('[DELETE DEBUG] Assemblies to update:', pointsByAssembly.size);

      // Get current user scale
      const userScale = useUnityBuildStore.getState().userScale;

      try {
        // Fire-and-forget updates for optimistic UX
        for (const [assemblyId, { assembly, remainingPoints }] of pointsByAssembly) {
          const updatedAssembly = {
            ...assembly,
            pointsList: remainingPoints,
          };

          console.log(
            `[DELETE DEBUG] Updating assembly ${assemblyId} with ${remainingPoints.length} points`
          );

          void submitAssembly({
            shouldUpdateBreakout: false,
            assembly: updatedAssembly,
            isUpdating: true,
            pageScale: userScale,
            shouldClearCurrentTakeoffAssembly: false, // Don't clear to handle multiple assemblies
          });
        }

        // Immediately clear selection and preview for responsiveness
        useSelectionStore.getState().setSelectedPoints([]);
        clearPastePreview();
        pastePositionRef.current = null;
        (window as any).__pastePreviewActive = false;
        (window as any).__pastePositionFrozen = false;
        (window as any).__frozenPastePosition = null;
        (window as any).__currentPastePreviewOffset = null;

        console.log('[DELETE DEBUG] Delete operation completed');
      } catch (error) {
        console.error('[DELETE DEBUG] Error deleting points:', error);
        // Clear updating flags on error
        markAssembliesUpdated(Array.from(affectedAssemblyIds));
      }
    };

    // NEW: Add handler for paste action that performs the actual paste and saves assemblies
    const handlePasteAction = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { copyMode, copiedObjects } = customEvent.detail;

      if (!copiedObjects || copiedObjects.length === 0) {
        console.log('[PASTE ACTION] No objects to paste');
        return;
      }

      // Use the exact transformed coordinates from the paste preview
      const transformedCoordinates = (window as any).__transformedPasteCoordinates;
      if (!transformedCoordinates || transformedCoordinates.length === 0) {
        console.log('[PASTE ACTION] No transformed preview coordinates available');
        return;
      }

      // Mark affected assemblies as updating BEFORE we start
      const affectedAssemblyIds = new Set<string>();
      if (copyMode === 'extend') {
        // For extend mode, mark the existing assembly as updating
        copiedObjects.forEach((obj: any) => {
          if (obj.assemblyProperties?.assemblyid) {
            affectedAssemblyIds.add(obj.assemblyProperties.assemblyid);
          }
        });
      } else {
        // For new assemblies, we don't need to mark anything as updating since they're new
      }
      affectedAssemblyIds.forEach(id => markAssemblyUpdating(id));

      // Get required services and state
      const userScale = useUnityBuildStore.getState().userScale;
      const currentObject = useUnityBuildStore.getState().selectedObject;

      // Note: copiedObjects already have flip transformation applied from handlePaste
      const transformedCopiedObjects = copiedObjects;

      try {
        // Different behavior based on copy mode
        if (copyMode === 'extend') {
          // For extend mode, verify we're still on the same document
          if (transformedCopiedObjects[0].assemblyProperties.objectid !== currentObject?.id) {
            console.log('[PASTE ACTION] Cannot extend assembly to a different document');
            document.dispatchEvent(new CustomEvent('app:clearpastepreview'));
            useSelectionStore.getState().clearCopiedItems();
            return;
          }

          // Track assemblies that need to be updated
          const assembliesToUpdate = new Map();

          // Process each copied object (group of points from a single assembly)
          for (const copiedObject of transformedCopiedObjects) {
            const { points, assemblyProperties } = copiedObject;
            const assemblyId = assemblyProperties.assemblyid;

            // Find the original assembly from the current list
            const originalAssembly = assembliesList.find((a: any) => a.assemblyid === assemblyId);
            if (!originalAssembly) {
              console.log(`[PASTE ACTION] Could not find assembly ${assemblyId} to extend`);
              continue;
            }

            // Create or get the updated assembly
            let updatedAssembly = assembliesToUpdate.get(assemblyId) || {
              ...originalAssembly,
            };

            // Find the corresponding transformed coordinates
            const transformedObj = transformedCoordinates.find(
              (tc: any) => tc.assemblyProperties?.assemblyid === assemblyId
            );
            if (!transformedObj) {
              console.log(
                `[PASTE ACTION] No transformed coordinates found for assembly ${assemblyId}`
              );
              continue;
            }

            // Create new points using the exact transformed coordinates from the preview
            const newPoints = points.map((point: Point, index: number) => {
              const transformedPoint = transformedObj.points.find(
                (tp: any) => tp.pointid === point.pointid
              );
              return {
                ...point,
                pointid: uuidv4(), // Generate new unique ID for each point
                x: transformedPoint ? transformedPoint.x : point.x, // Use exact preview coordinates
                y: transformedPoint ? transformedPoint.y : point.y, // Use exact preview coordinates
                isselected: false, // Reset selection state
                iscopied: false, // Reset copied state
                // Intelligently set allowtrailingline: first point should be false for clean takeoff
                allowtrailingline: index === 0 ? false : point.allowtrailingline,
              };
            });

            // Add new points to the assembly
            updatedAssembly = {
              ...updatedAssembly,
              pointsList: [...(updatedAssembly.pointsList || []), ...newPoints],
            };

            // Store the updated assembly
            assembliesToUpdate.set(assemblyId, updatedAssembly);
          }

          // Fire-and-forget updates for optimistic UX
          Array.from(assembliesToUpdate.values()).forEach(assembly => {
            void submitAssembly({
              shouldUpdateBreakout: false,
              assembly,
              isUpdating: true,
              pageScale: userScale,
              shouldClearCurrentTakeoffAssembly: false, // Don't clear so we can handle multiple assemblies
            });
          });
        } else {
          // Process each copied object, and consider the current objectId and breakoutId when copy as new
          for (const copiedObject of transformedCopiedObjects) {
            const { points, assemblyProperties } = copiedObject;

            // Create new assembly with new ID based on properties of original
            const newAssemblyId = uuidv4();

            // Find the corresponding transformed coordinates
            const transformedObj = transformedCoordinates.find(
              (tc: any) => tc.assemblyProperties?.assemblyid === assemblyProperties.assemblyid
            );
            if (!transformedObj) {
              console.log(
                `[PASTE ACTION] No transformed coordinates found for assembly ${assemblyProperties.assemblyid}`
              );
              continue;
            }

            // Create new points using the exact transformed coordinates from the preview
            const newPoints = points.map((point: Point, index: number) => {
              const transformedPoint = transformedObj.points.find(
                (tp: any) => tp.pointid === point.pointid
              );
              return {
                ...point,
                pointid: uuidv4(), // Generate new unique ID for each point
                x: transformedPoint ? transformedPoint.x : point.x, // Use exact preview coordinates
                y: transformedPoint ? transformedPoint.y : point.y, // Use exact preview coordinates
                isselected: false, // Reset selection state
                iscopied: false, // Reset copied state
                // Intelligently set allowtrailingline: first point should be false for clean takeoff
                allowtrailingline: index === 0 ? false : point.allowtrailingline,
              };
            });

            // Create new assembly
            const newAssembly: TakeoffAssembly = {
              ...assemblyProperties,
              assemblyid: newAssemblyId,
              assemblyname: assemblyProperties.assemblyname,
              pointsList: newPoints,
              objectid: currentObject?.id || '',
            };

            // Save the new assembly (non-blocking)
            void submitAssembly({
              shouldUpdateBreakout: true,
              assembly: newAssembly,
              isCreating: true,
              pageScale: userScale,
              shouldClearCurrentTakeoffAssembly: false, // Don't clear so we can handle multiple assemblies
            });
          }
        }

        // Immediately clear preview and notify completion for snappy UX
        clearPastePreview();
        pastePositionRef.current = null;
        (window as any).__frozenPastePosition = null;
        (window as any).__currentPastePreviewOffset = null;
        useUnityBuildStore
          .getState()
          .setIsEditingPoint({ isEditingPoint: false, isViewingTakeoffEditor: false });
        document.dispatchEvent(new CustomEvent('app:paste:complete'));
        useAssemblyStore.getState().setCurrentTakeoffAssembly(null);
      } catch (error) {
        console.error('[PASTE ACTION] Error during paste operation:', error);
        // Clear updating flags on error
        markAssembliesUpdated(Array.from(affectedAssemblyIds));
      }
    };

    document.addEventListener('app:pastepreview', handlePastePreviewEvent);
    document.addEventListener('app:escapepress', handleEscapeEvent);
    document.addEventListener('app:copy', handleCopyEvent);
    document.addEventListener('app:clearpastepreview', handleClearPastePreview);
    document.addEventListener('app:paste', handlePasteEvent);
    document.addEventListener('app:paste:action', handlePasteAction);
    document.addEventListener('app:delete', handleDeleteEvent);

    return () => {
      document.removeEventListener('app:pastepreview', handlePastePreviewEvent);
      document.removeEventListener('app:escapepress', handleEscapeEvent);
      document.removeEventListener('app:copy', handleCopyEvent);
      document.removeEventListener('app:clearpastepreview', handleClearPastePreview);
      document.removeEventListener('app:paste', handlePasteEvent);
      document.removeEventListener('app:paste:action', handlePasteAction);
      document.removeEventListener('app:delete', handleDeleteEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activatePastePreview,
    clearPastePreview,
    copiedObjects.length,
    setCopiedObjects,
    handleCopy,
    handlePaste,
    assembliesList,
    submitAssembly,
    isAnnotating,
    setIsAnnotating,
  ]);

  // Expose all event handlers and state
  return {
    // Mouse event handlers
    handleTakeoffButton0MouseEvents,
    handleTakeoffButton2MouseEvents,
    handleMouseMove,
    handleMouseUp,

    // Clipboard operations
    handleCopy,
    handlePaste,
    handleKeyDown,

    // Selection and preview state
    selectedPoints,
    isDragSelecting,
    dragStartPosition,
    showPastePreview,
    renderPastePreview,
    clearPastePreview,

    // Context menu
    showContextMenu,
    setShowContextMenu,

    // Cursor position
    cursorPositionRef,
    cursorPosition,
  };
};
